import React, { useState, useEffect } from 'react';
import { FileText, Search, Plus, X, Loader2, Eye, Tag, Edit, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import ConfirmDialog from '../shared/ConfirmDialog';

const policyCategories = ['Infection Control', 'Patient Safety', 'Clinical Protocols', 'Emergency Procedures', 'Regulatory Compliance', 'HR & Administrative'];
const emptyForm = { title: '', category: '', description: '', content: '', department: '', effective_date: '', review_date: '' };

const PoliciesPage = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadPolicies = () => { setLoading(true); api.get('/api/policies').then(setPolicies).catch(e => toast.error(e.message)).finally(() => setLoading(false)); };
  useEffect(() => { loadPolicies(); }, []);

  const openCreate = () => { setForm(emptyForm); setEditingId(null); setShowModal(true); };
  const openEdit = (p) => {
    setForm({ title: p.title || '', category: p.category || '', description: p.description || '', content: p.content || '', department: p.department || '', effective_date: p.effective_date || '', review_date: p.review_date || '' });
    setEditingId(p.id); setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.title || !form.category || !form.content || !form.effective_date || !form.review_date) { toast.error('Fill required fields'); return; }
    setSubmitting(true);
    try {
      if (editingId) { await api.put(`/api/policies/${editingId}`, form); toast.success('Policy updated'); }
      else { await api.post('/api/policies', form); toast.success('Policy created'); }
      setShowModal(false); setForm(emptyForm); setEditingId(null); loadPolicies();
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return; setDeleting(true);
    try { await api.delete(`/api/policies/${deleteTarget.id}`); toast.success('Policy deleted'); setDeleteTarget(null); loadPolicies(); }
    catch (e) { toast.error(e.message); } finally { setDeleting(false); }
  };

  const filteredPolicies = policies.filter(p => {
    const matchesSearch = p.title?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || p.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6" data-testid="policies-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div><h1 className="text-2xl font-bold text-gray-900">Policies & Procedures</h1><p className="text-gray-500 text-sm mt-1">Access and manage facility policies</p></div>
        <Button onClick={openCreate} className="btn-primary gap-2" data-testid="create-policy-btn"><Plus className="w-4 h-4" />Create Policy</Button>
      </div>

      <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" /><Input placeholder="Search policies..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 h-12" data-testid="search-policies-input" /></div>

      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setSelectedCategory('all')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedCategory === 'all' ? 'bg-[var(--niko-teal)] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>All ({policies.length})</button>
        {policyCategories.map(cat => (
          <button key={cat} onClick={() => setSelectedCategory(cat === selectedCategory ? 'all' : cat)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${selectedCategory === cat ? 'bg-[var(--niko-teal)] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{cat}</button>
        ))}
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[var(--niko-teal)]" /></div> : (
        <div className="bg-white rounded-xl border border-gray-100">
          <div className="divide-y divide-gray-100">
            {filteredPolicies.length === 0 ? <div className="p-8 text-center text-gray-400">{policies.length === 0 ? 'No policies yet.' : 'No match'}</div> :
            filteredPolicies.map(policy => (
              <div key={policy.id} className="p-4 hover:bg-gray-50 transition-colors group" data-testid="policy-item">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[var(--niko-teal)]/10 flex items-center justify-center flex-shrink-0"><FileText className="w-5 h-5 text-[var(--niko-teal)]" /></div>
                    <div>
                      <h3 className="font-medium text-gray-900">{policy.title}</h3>
                      <div className="flex items-center gap-3 text-sm text-gray-500 mt-1"><span className="flex items-center gap-1"><Tag className="w-3 h-3" />{policy.category}</span><span>v{policy.version}</span><span>Effective: {policy.effective_date}</span></div>
                      {policy.description && <p className="text-sm text-gray-500 mt-1">{policy.description}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${policy.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{policy.status}</span>
                    <button onClick={() => openEdit(policy)} className="p-2 text-gray-400 hover:text-[var(--niko-teal)] hover:bg-[var(--niko-teal)]/10 rounded-lg opacity-0 group-hover:opacity-100 transition-all" data-testid="edit-policy-btn"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => setDeleteTarget(policy)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all" data-testid="delete-policy-btn"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="policy-modal">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">{editingId ? 'Edit Policy' : 'Create Policy'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><Label>Title *</Label><Input className="mt-1" value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="policy-title-input" /></div>
              <div><Label>Category *</Label><select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.category} onChange={e => setForm({...form, category: e.target.value})}><option value="">Select...</option>{policyCategories.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
              <div><Label>Description</Label><Input className="mt-1" value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
              <div><Label>Content *</Label><Textarea className="mt-1" rows={6} value={form.content} onChange={e => setForm({...form, content: e.target.value})} /></div>
              <div><Label>Department</Label><Input className="mt-1" value={form.department} onChange={e => setForm({...form, department: e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Effective Date *</Label><Input type="date" className="mt-1" value={form.effective_date} onChange={e => setForm({...form, effective_date: e.target.value})} /></div>
                <div><Label>Review Date *</Label><Input type="date" className="mt-1" value={form.review_date} onChange={e => setForm({...form, review_date: e.target.value})} /></div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex gap-3">
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting} className="flex-1 btn-primary" data-testid="submit-policy-btn">{submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{editingId ? 'Update' : 'Create'}</Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} loading={deleting} title="Delete Policy" message={`Delete "${deleteTarget?.title}"? This cannot be undone.`} />
    </div>
  );
};

export default PoliciesPage;
